import { Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import ListTableDataRow from './ListTableDataRow';
import ListTableGroupRow from './ListTableGroupRow';
import ListTableHeader from './ListTableHeader';
import ListTableTailRow from './ListTableTailRow';
import { tableSx } from './styles';
import { VirtualTableRowType } from './types';
import type { ListTableProps, ListTableRow } from './types';
import useListTableVirtualization from './useListTableVirtualization';

export type { ListTableProps } from './types';

const DEFAULT_COLUMN_WIDTH = 160;

const ListTable = <Row extends ListTableRow>(props: ListTableProps<Row>) => {
  const {
    columns,
    withChildren = false,
    columnVisibilityControl,
    defaultColumnWidth = DEFAULT_COLUMN_WIDTH,
    rowActions = { actions: [] },
    menuActions = [],
    mainAction,
    sort = null,
    onSortChange,
    emptyContent,
    isRowHighlighted,
    sx,
    containerSx,
    virtualHeight,
    rowHeight = 48,
    groupRowHeight = 40,
    groups,
  } = props;
  const {
    containerRef,
    setScrollTop,
    virtualRows,
    visibleRows,
    topSpacerHeight,
    bottomSpacerHeight,
    toggleGroup,
    toggleRow,
  } = useListTableVirtualization(props);
  const hasActions = Boolean(mainAction) || Boolean(rowActions.actions?.length) || menuActions.length > 0;
  const columnCount = columns.length + (hasActions ? 1 : 0);

  return (
    <TableContainer
      ref={containerRef}
      onScroll={event => setScrollTop(event.currentTarget.scrollTop)}
      sx={[
        {
          flex: '1 1 auto',
          minHeight: 0,
          width: 'fit-content',
          maxWidth: '100%',
          height: virtualHeight ?? '100%',
          maxHeight: virtualHeight,
          overflow: 'auto',
        },
        ...(Array.isArray(containerSx) ? containerSx : containerSx ? [containerSx] : []),
      ]}
    >
      <Table stickyHeader sx={[tableSx, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}>
        <ListTableHeader
          columns={columns}
          columnVisibilityControl={columnVisibilityControl}
          defaultColumnWidth={defaultColumnWidth}
          hasActions={hasActions}
          indentFirstColumn={Boolean(groups) || withChildren}
          mainAction={mainAction}
          sort={sort}
          onSortChange={onSortChange}
        />
        <TableBody>
          {topSpacerHeight > 0 && (
            <TableRow>
              <TableCell colSpan={columnCount} sx={{ p: 0, height: topSpacerHeight, border: 0 }} />
            </TableRow>
          )}
          {visibleRows.map(item => {
            if (item.type === VirtualTableRowType.GROUP)
              return (
                <ListTableGroupRow
                  key={`group-${item.group.id}`}
                  group={item.group}
                  isExpanded={item.isExpanded}
                  columnCount={columnCount}
                  groupRowHeight={groupRowHeight}
                  onToggle={toggleGroup}
                />
              );
            if (item.type === VirtualTableRowType.TAIL)
              return (
                <ListTableTailRow
                  key={item.group ? `tail-${item.group.id}` : 'tail'}
                  item={item}
                  columnCount={columnCount}
                  rowHeight={rowHeight}
                />
              );
            return (
              <ListTableDataRow
                key={item.type === VirtualTableRowType.CHILDREN ? item.key : item.row.id}
                item={item}
                columns={columns}
                defaultColumnWidth={defaultColumnWidth}
                rowHeight={rowHeight}
                hasActions={hasActions}
                rowActions={rowActions}
                menuActions={menuActions}
                isRowHighlighted={isRowHighlighted}
                onToggleRow={toggleRow}
              />
            );
          })}
          {bottomSpacerHeight > 0 && (
            <TableRow>
              <TableCell colSpan={columnCount} sx={{ p: 0, height: bottomSpacerHeight, border: 0 }} />
            </TableRow>
          )}
          {!virtualRows.length && (
            <TableRow>
              <TableCell colSpan={columnCount}>
                <Typography color="textSecondary">{emptyContent ?? 'No records found.'}</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ListTable;
